/**
 * Utilities for cleaning up course requisite text coming from SIS/catalog data.
 *
 * Motivation (PLAN-935): some PeopleSoft configurations describe a course's
 * requisite using only the course's own code, sometimes wrapped in label text
 * like "Requirement for MTHM 168" or "ECON 203 Requirements". The data is
 * meaningless to students and triggers a spurious Requisite alert. We can't
 * fix the upstream data quickly, so we filter these self-reference labels
 * defensively at the consumer.
 */

const normalizeCourseCode = (value: string): string => value.replace(/\s+/g, '').toUpperCase()

// Recognise a course code as an isolated token: 2-8 letters, mandatory
// whitespace, 3-4 digits, optional 1-3 trailing letters (e.g. "211L", "150CL").
// The mandatory whitespace prevents accidental matches like "year2025".
const COURSE_CODE_TOKEN = /\b[A-Za-z]{2,8}\s+\d{3,4}[A-Za-z]{0,3}\b/g

// Words that are pure label/connector noise around a self-reference.
// Conservative list: only words that carry no semantic prereq content.
// "and"/"or"/"with" are intentionally NOT here — they usually attach a
// substantive clause (e.g. "MTHM 168 with grade of C") which we want to keep.
const NOISE_WORDS = new Set([
  'requirement',
  'requirements',
  'prerequisite',
  'prerequisites',
  'pre-requisite',
  'pre-requisites',
  'prereq',
  'prereqs',
  'pre-req',
  'pre-reqs',
  'corequisite',
  'corequisites',
  'co-requisite',
  'co-requisites',
  'required',
  'requires',
  'reqs',
  'for',
  'of',
  'the'
])

/**
 * Filter a requisite string when its only meaningful content is the course
 * referencing itself.
 *
 * Returns `null` when:
 *   - The whole trimmed/normalised string equals the course's own code
 *     (e.g. "MTHM 168", "  mthm 168  ", "MTHM168").
 *   - Or every course-code-shaped token in the string is the course's own
 *     code AND what remains after stripping those tokens is only label-noise
 *     (e.g. "Requirement for MTHM 168", "ECON 203 Requirements").
 *
 * Returns the original string in every other case, including:
 *   - A list that mentions the course alongside other real prereqs
 *     ("MATH 100, MATH 101, PYS 200").
 *   - A clause where the self-code is followed by substantive content
 *     ("MTHM 168 with grade of C or better").
 *   - Free-form prose ("Junior standing", "Departmental approval").
 */
export function filterSelfReferencingRequisite(
  requisite: string | null | undefined,
  subjectId: string | null | undefined,
  courseNumber: string | null | undefined
): string | null {
  if (!requisite) {
    return null
  }
  if (!subjectId || !courseNumber) {
    return requisite
  }

  const selfCode = normalizeCourseCode(`${subjectId}${courseNumber}`)

  // Fast path: whole-string self-reference (covers "MTHM168" without a space
  // and other shapes the token regex below cannot match because of strict
  // whitespace requirements).
  if (normalizeCourseCode(requisite) === selfCode) {
    return null
  }

  const tokens = requisite.match(COURSE_CODE_TOKEN) ?? []

  // No course-code tokens → free-form prose (e.g. "Junior standing"). The
  // whole-string fast path above already handled the bare-code case, so
  // there is nothing more to filter.
  if (tokens.length === 0) {
    return requisite
  }

  // If even one token names a different course, the string carries at least
  // one real prereq. Leave it alone so the SIS data issue stays visible.
  const allTokensAreSelf = tokens.every((t) => normalizeCourseCode(t) === selfCode)
  if (!allTokensAreSelf) {
    return requisite
  }

  // All tokens are self-references. Strip them and inspect what's left.
  const remainder = requisite.replace(COURSE_CODE_TOKEN, ' ')
  // Preserve internal hyphens so label words like "pre-req" / "co-requisite"
  // survive the split and match their entries in NOISE_WORDS.
  const remainderWords = remainder
    .split(/[\s.,;:()/\\[\]{}'"!?]+/)
    .map((w) => w.trim().toLowerCase())
    .filter((w) => w.length > 0)

  if (remainderWords.length === 0) {
    return null
  }
  if (remainderWords.every((w) => NOISE_WORDS.has(w))) {
    return null
  }

  return requisite
}

export default filterSelfReferencingRequisite
